/** Parsed per-metric insight block from blueprint ``sections.summary`` markdown. */

export interface MetricInsight {
  metricName: string;
  scoreLabel: string;
  whatWorks: string;
  whatDoesNot: string;
  actionableNotes: string;
}

function extractSection(text: string, heading: string): string {
  const re = new RegExp(
    `\\*\\*${heading.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\*\\*\\s*\\n\\n([\\s\\S]*?)(?=\\n\\n\\*\\*|$)`,
    "i"
  );
  const m = text.match(re);
  return m ? m[1].trim() : "";
}

export function normalizeMetricKey(name: string): string {
  return name.toLowerCase().replace(/\s+/g, " ").trim();
}

/** Parse V7 summary markdown into a lookup keyed by normalised metric name. */
export function parseBlueprintSummary(markdown: string): Map<string, MetricInsight> {
  const map = new Map<string, MetricInsight>();
  if (!markdown?.trim()) return map;

  const blocks = markdown.split(/\n(?=\*\*[^*]+:\s*[^*]+\*\*)/);
  for (const block of blocks) {
    const headerMatch = block.match(/^\*\*(.+?):\s*([^*]+?)\*\*/);
    if (!headerMatch) continue;
    const metricName = headerMatch[1].trim();
    const scoreLabel = headerMatch[2].trim();
    const insight: MetricInsight = {
      metricName,
      scoreLabel,
      whatWorks: extractSection(block, "What works"),
      whatDoesNot: extractSection(block, "What does not"),
      actionableNotes: extractSection(block, "Actionable notes"),
    };
    map.set(normalizeMetricKey(metricName), insight);
  }
  return map;
}

export function lookupInsight(
  insights: Map<string, MetricInsight>,
  metricLabel: string
): MetricInsight | undefined {
  return insights.get(normalizeMetricKey(metricLabel));
}

/** First non-empty "what works" line across sub-metrics — pillar-level summary. */
export function pillarSummaryFromInsights(
  labels: string[],
  insights: Map<string, MetricInsight>
): string {
  for (const label of labels) {
    const ins = lookupInsight(insights, label);
    if (ins?.whatWorks?.trim()) {
      const line = ins.whatWorks.split("\n")[0]?.trim();
      if (line) return line;
    }
  }
  return "";
}
